<template>
  <div class="marketing-page slide-manager">
    <MarketingToolHeader
      eyebrow="App Content"
      title="Slide Manager"
      description="Plane Slider-Motive für die App und steuere Zeitraum, Zielstudios und Weiterleitung an einem Ort."
      icon="mdi-view-carousel-outline">
      <template v-slot:actions>
        <v-btn icon class="icon-action" :loading="loading" aria-label="Slides aktualisieren" title="Slides aktualisieren" @click="fetchSlides">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
        <v-btn color="#009640" depressed class="primary-action" @click="createNewSlide"><v-icon left>mdi-plus</v-icon>Neue Slide</v-btn>
      </template>
    </MarketingToolHeader>

    <section class="metric-grid" aria-label="Slide Übersicht">
      <v-card dark flat class="metric-card"
        ><span class="metric-icon icon-green"><v-icon>mdi-broadcast</v-icon></span>
        <div>
          <span>Aktiv</span><strong>{{ loading ? "–" : sortedSlides.active.length }}</strong
          ><small>aktuell sichtbar</small>
        </div></v-card
      >
      <v-card dark flat class="metric-card"
        ><span class="metric-icon icon-amber"><v-icon>mdi-clock-outline</v-icon></span>
        <div>
          <span>Geplant</span><strong>{{ loading ? "–" : sortedSlides.upcoming.length }}</strong
          ><small>starten später</small>
        </div></v-card
      >
      <v-card dark flat class="metric-card"
        ><span class="metric-icon"><v-icon>mdi-archive-outline</v-icon></span>
        <div>
          <span>Archiv</span><strong>{{ loading ? "–" : sortedSlides.past.length }}</strong
          ><small>abgelaufene Slides</small>
        </div></v-card
      >
      <v-card dark flat class="metric-card"
        ><span class="metric-icon"><v-icon>mdi-view-carousel</v-icon></span>
        <div>
          <span>Gesamt</span><strong>{{ loading ? "–" : slides.length }}</strong
          ><small>alle Motive</small>
        </div></v-card
      >
    </section>

    <v-card dark flat class="content-surface">
      <div class="surface-header">
        <div>
          <div class="surface-eyebrow">Ausspielung</div>
          <h2>Aktiv &amp; geplant</h2>
          <p>Alle laufenden und vorbereiteten Slider-Motive.</p>
        </div>
        <span class="count-pill">{{ sortedSlides.active.length + sortedSlides.upcoming.length }} Slides</span>
      </div>

      <div v-if="sortedSlides.active.length || sortedSlides.upcoming.length" class="slide-grid">
        <v-card
          v-for="item in [...sortedSlides.active, ...sortedSlides.upcoming]"
          :key="item._id"
          dark
          flat
          class="slide-card"
          role="button"
          tabindex="0"
          @click="editSlide(item)"
          @keydown.enter="editSlide(item)">
          <div class="slide-media">
            <v-img
              v-if="item.image"
              :src="'/img/' + item.image"
              height="190"
              class="slide-image"
              gradient="to top, rgba(16,16,17,.9), rgba(16,16,17,.05) 70%">
              <div class="image-title">{{ trimLength(item.title, 46) }}</div>
            </v-img>
            <div v-else class="image-placeholder"><v-icon>mdi-image-off-outline</v-icon></div>
            <span class="status-pill" :class="item.status.colorClass"
              ><v-icon small>{{ item.status.icon }}</v-icon
              >{{ item.status.text }}</span
            >
          </div>
          <div class="slide-content">
            <div class="studio-chips">
              <span v-for="studio in (item.studios || []).slice(0, 3)" :key="studio._id || studio" class="mini-chip">{{
                getStudioName(studio)
              }}</span>
              <span v-if="(item.studios || []).length > 3" class="mini-chip">+{{ item.studios.length - 3 }}</span>
            </div>
            <div class="card-meta">
              <span
                ><v-icon small>mdi-calendar-range-outline</v-icon>{{ $moment(item.showAfter).format("DD.MM.YY") }} –
                {{ $moment(item.hideAfter).format("DD.MM.YY") }}</span
              >
              <v-icon small>mdi-arrow-right</v-icon>
            </div>
          </div>
        </v-card>
      </div>

      <div v-else-if="!loading" class="surface-empty">
        <span><v-icon>mdi-view-carousel-outline</v-icon></span>
        <h3>Keine laufenden Slides</h3>
        <p>Plane das nächste Motiv für den App-Slider.</p>
        <v-btn outlined color="#58d388" class="secondary-action" @click="createNewSlide"><v-icon left>mdi-plus</v-icon>Slide erstellen</v-btn>
      </div>
    </v-card>

    <v-card v-if="sortedSlides.past.length" dark flat class="content-surface archive-surface">
      <div class="surface-header">
        <div>
          <div class="surface-eyebrow">Historie</div>
          <h2>Archiv</h2>
          <p>Abgelaufene Slides für spätere Kampagnen wiederverwenden.</p>
        </div>
        <span class="count-pill">{{ sortedSlides.past.length }} archiviert</span>
      </div>
      <div class="archive-grid">
        <v-card
          v-for="item in paginatedArchive"
          :key="item._id"
          dark
          flat
          class="archive-card"
          role="button"
          tabindex="0"
          @click="editSlide(item)"
          @keydown.enter="editSlide(item)">
          <v-img v-if="item.image" :src="'/img/' + item.image" height="105" class="archive-image" />
          <div v-else class="archive-placeholder"><v-icon>mdi-image-off-outline</v-icon></div>
          <div>
            <span><v-icon small>mdi-archive-outline</v-icon>Archiviert</span>
            <h3>{{ trimLength(item.title, 40) }}</h3>
          </div>
        </v-card>
      </div>
      <v-pagination v-if="archivePageCount > 1" v-model="archivePage" :length="archivePageCount" class="archive-pagination" dark color="#009640" />
    </v-card>

    <v-dialog
      v-model="showEditDialog"
      max-width="940px"
      persistent
      scrollable
      :fullscreen="$vuetify.breakpoint.xsOnly"
      content-class="marketing-editor-dialog">
      <v-card v-if="slide" dark class="editor-card">
        <div class="editor-header">
          <span class="editor-mark"><v-icon>mdi-view-carousel-outline</v-icon></span>
          <div>
            <div class="surface-eyebrow">Slide Editor</div>
            <h2>{{ slide._id ? "Slide bearbeiten" : "Neue Slide erstellen" }}</h2>
            <p>Motiv, Link, Laufzeit und Zielstudios konfigurieren.</p>
          </div>
          <v-btn icon class="icon-action editor-close" aria-label="Dialog schliessen" @click="showEditDialog = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </div>

        <v-card-text class="editor-body">
          <v-row>
            <v-col cols="12" md="7">
              <section class="editor-section">
                <div class="section-heading">
                  <v-icon>mdi-text-box-outline</v-icon>
                  <div>
                    <h3>Inhalt &amp; Ziel</h3>
                    <p>Titel, Weiterleitung und Studios</p>
                  </div>
                </div>
                <v-text-field
                  v-model="slide.title"
                  label="Titel"
                  placeholder="z.B. Sommer-Aktion"
                  outlined
                  dark
                  class="modern-field"
                  hide-details="auto" />
                <v-text-field
                  v-model="slide.link"
                  label="Link"
                  placeholder="https://..."
                  prepend-inner-icon="mdi-link-variant"
                  outlined
                  dark
                  class="modern-field"
                  hide-details="auto" />
                <v-select
                  v-model="slide.studios"
                  :items="studios"
                  item-text="name"
                  item-value="_id"
                  label="Studios"
                  multiple
                  chips
                  outlined
                  dark
                  attach
                  class="modern-field"
                  hide-details="auto" />
              </section>
            </v-col>
            <v-col cols="12" md="5">
              <section class="editor-section">
                <div class="section-heading">
                  <v-icon>mdi-image-outline</v-icon>
                  <div>
                    <h3>Motiv &amp; Laufzeit</h3>
                    <p>Breites Sliderbild und Sichtbarkeit</p>
                  </div>
                </div>
                <slim-cropper :key="slide.image" class="image-cropper" :options="slimOptionsExercise">
                  <img v-if="slide.image" :src="'/img/' + slide.image" alt="Slide Bild" />
                  <input type="file" accept="image/*" />
                </slim-cropper>
                <span class="format-hint">Empfohlenes Format: 3:1 · 900 × 300 px</span>
                <v-date-picker v-model="datePicker" locale="de-DE" range no-title dark full-width color="#009640" class="date-picker" />
              </section>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="editor-actions">
          <span class="validation-hint">Titel, Bild, Zeitraum und mindestens ein Studio sind erforderlich.</span>
          <v-spacer />
          <v-btn text class="cancel-action" @click="showEditDialog = false">Abbrechen</v-btn>
          <v-btn color="#009640" depressed class="primary-action" :disabled="!isFormValid" :loading="working" @click="saveSlide">
            <v-icon left>mdi-content-save-outline</v-icon>Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Slim from "../../../plugins/slimImage/slim/slim.vue"
import api from "../../../api"
import MarketingToolHeader from "./MarketingToolHeader.vue"

export default {
  name: "slideManager",
  components: { MarketingToolHeader, "slim-cropper": Slim },
  data() {
    return {
      studios: [],
      loading: false,
      working: false,
      showEditDialog: false,
      datePicker: [],
      slides: [],
      slide: null,
      archivePage: 1,
      archiveItemsPerPage: 8,
      slimOptionsExercise: {
        ratio: "3:1",
        initialImage: "/img/einmalig/imageUploadPlaceholder.jpg",
        buttonCancelLabel: "Abbrechen",
        buttonConfirmLabel: "Bestätigen",
        label: "Bild hochladen",
        service: this.uploadPicture,
        serviceFormat: "file",
        instantEdit: true,
        push: true,
      },
    }
  },
  computed: {
    sortedSlides() {
      const categories = { active: [], upcoming: [], past: [] }
      if (!this.slides) return categories
      const sortedByDate = [...this.slides].sort((a, b) => new Date(b.showAfter) - new Date(a.showAfter))

      sortedByDate.forEach((item) => {
        if (!this.$moment(item.showAfter).isValid() || !this.$moment(item.hideAfter).isValid()) {
          categories.past.push({ ...item, status: this.getStatusDetails("past") })
          return
        }
        const now = this.$moment()
        const from = this.$moment(item.showAfter)
        const to = this.$moment(item.hideAfter).endOf("day")
        let status
        if (now.isBetween(from, to)) status = "active"
        else if (now.isAfter(to)) status = "past"
        else status = "upcoming"
        categories[status].push({ ...item, status: this.getStatusDetails(status, from) })
      })
      return categories
    },
    paginatedArchive() {
      const start = (this.archivePage - 1) * this.archiveItemsPerPage
      return this.sortedSlides.past.slice(start, start + this.archiveItemsPerPage)
    },
    archivePageCount() {
      return Math.ceil(this.sortedSlides.past.length / this.archiveItemsPerPage)
    },
    isFormValid() {
      if (!this.slide) return false
      const { title, studios, showAfter, hideAfter, image } = this.slide
      return title && title.length > 2 && studios && studios.length > 0 && showAfter && hideAfter && image
    },
  },
  created() {
    this.fetchSlides()
    this.initSlide()
  },
  methods: {
    initSlide() {
      this.datePicker = []
      this.slide = { _id: null, hideAfter: null, showAfter: null, link: "", image: "", title: "", studios: [] }
    },
    getStatusDetails(status, from) {
      const now = this.$moment()
      switch (status) {
        case "active":
          return { text: "Aktiv", icon: "mdi-broadcast", colorClass: "is-visible" }
        case "upcoming": {
          const diff = from.diff(now, "days") + 1
          return { text: `Startet in ${diff} Tag(en)`, icon: "mdi-clock-start", colorClass: "is-upcoming" }
        }
        default:
          return { text: "Archiviert", icon: "mdi-archive-outline", colorClass: "is-past" }
      }
    },
    editSlide(item) {
      this.slide = JSON.parse(JSON.stringify(item))
      delete this.slide.status
      this.slide.studios = this.slide.studios.map((studio) => studio._id || studio)
      this.datePicker =
        this.slide.showAfter && this.slide.hideAfter
          ? [this.$moment(this.slide.showAfter).format("YYYY-MM-DD"), this.$moment(this.slide.hideAfter).format("YYYY-MM-DD")]
          : []
      this.showEditDialog = true
    },
    createNewSlide() {
      this.initSlide()
      this.showEditDialog = true
    },
    getStudioName(studioEntry) {
      if (studioEntry && typeof studioEntry === "object" && studioEntry.name) return studioEntry.name
      const studioId = studioEntry && typeof studioEntry === "object" ? studioEntry._id : studioEntry
      const studio = this.studios.find((item) => item._id === studioId)
      return studio ? studio.name : "Studio"
    },
    async fetchSlides() {
      this.loading = true
      try {
        const response = await api.fetchSlides()
        this.studios = response.studios
        this.slides = response.slides
        this.$emit("finishedloading")
      } catch (e) {
        this.$toast.error("Fehler beim Laden der Slides.")
      } finally {
        this.loading = false
      }
    },
    async saveSlide() {
      if (!this.isFormValid) {
        this.$toast.error("Bitte fülle alle Felder aus (Titel, Bild, Datum, Studios).")
        return
      }
      this.working = true
      try {
        await api.saveSlide(this.slide)
        this.$toast.success("Slide erfolgreich gespeichert.")
        this.showEditDialog = false
        await this.fetchSlides()
      } catch (e) {
        this.$toast.error("Fehler beim Speichern der Slide.")
      } finally {
        this.working = false
      }
    },
    uploadPicture(formdata, progress, success, failure) {
      const formData = new FormData()
      formData.append("file", formdata[0])
      api
        .uploadImage({ art: "slide", formData })
        .then((data) => {
          if (data.status) {
            this.slide.image = data.name
            success(data)
          }
        })
        .catch((error) => failure(error))
    },
    trimLength(value, length) {
      if (!value) return ""
      return value.length > length ? value.substring(0, length) + "..." : value
    },
  },
  watch: {
    datePicker(value) {
      if (!this.slide) return
      if (value && value.length === 2) {
        const sorted = [...value].sort((a, b) => new Date(a) - new Date(b))
        this.slide.showAfter = sorted[0]
        this.slide.hideAfter = sorted[1]
      } else {
        this.slide.showAfter = null
        this.slide.hideAfter = null
      }
    },
  },
}
</script>

<style scoped>
.marketing-page {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  max-width: 92.5rem;
  margin: 0 auto;
  padding: 1.5rem;
  color: var(--text);
  font-size: 1rem;
}

.icon-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.primary-action,
.secondary-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.primary-action {
  color: #ffffff !important;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 1rem 0;
}

.metric-card {
  display: flex;
  min-height: 6.75rem;
  align-items: center;
  gap: 0.875rem;
  padding: 1.125rem;
  border: 1px solid var(--border);
  border-radius: 1.1875rem !important;
  background: var(--surface) !important;
}

.metric-icon,
.editor-mark,
.surface-empty > span {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
}

.metric-icon {
  width: 2.625rem;
  height: 2.625rem;
  border-radius: 0.8125rem;
  color: #c5c5c8;
  background: rgba(255, 255, 255, 0.065);
}

.icon-green {
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.icon-amber {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.12);
}

.metric-card > div {
  display: grid;
  min-width: 0;
}

.metric-card > div > span {
  color: var(--text-soft);
  font-size: 0.875rem;
  font-weight: 700;
}

.metric-card strong {
  color: #ffffff;
  font-size: 1.6875rem;
  line-height: 1.15;
}

.metric-card small {
  margin-top: 0.1875rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.content-surface {
  padding: 1.375rem;
  border: 1px solid var(--border);
  border-radius: 1.375rem !important;
  background: rgba(255, 255, 255, 0.03) !important;
  box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.12);
}

.archive-surface {
  margin-top: 1rem;
}

.surface-header,
.card-meta,
.editor-header,
.section-heading {
  display: flex;
  align-items: center;
}

.surface-header {
  justify-content: space-between;
  gap: 1.125rem;
  margin-bottom: 1.125rem;
}

.surface-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.surface-header h2,
.editor-header h2 {
  margin: 0.1875rem 0 0;
  color: var(--text);
  font-size: 1.25rem;
}

.surface-header p,
.editor-header p,
.section-heading p,
.surface-empty p {
  margin: 0.25rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.count-pill,
.mini-chip,
.status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
}

.count-pill {
  min-height: 1.75rem;
  padding: 0.25rem 0.625rem;
  border: 1px solid var(--border);
  color: var(--text-soft);
  background: rgba(255, 255, 255, 0.055);
  white-space: nowrap;
}

.slide-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.875rem;
}

.slide-card,
.archive-card {
  overflow: hidden;
  border: 1px solid var(--border);
  outline: none;
  background: var(--surface) !important;
  transition: transform 160ms ease, border-color 160ms ease, background-color 160ms ease;
}

.slide-card {
  border-radius: 1.125rem !important;
}

.slide-card:focus-visible,
.archive-card:focus-visible {
  border-color: rgba(0, 150, 64, 0.68);
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.slide-media {
  position: relative;
  height: 11.875rem;
  background: rgba(0, 0, 0, 0.2);
}

.slide-image,
.image-placeholder {
  height: 100%;
}

.image-placeholder,
.archive-placeholder {
  display: grid;
  place-items: center;
  color: #77797f;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.035), rgba(0, 0, 0, 0.18));
}

.image-placeholder .v-icon {
  color: currentColor;
  font-size: 3rem;
}

.image-title {
  position: absolute;
  right: 1rem;
  bottom: 0.875rem;
  left: 1rem;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 750;
  line-height: 1.35;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.7);
}

.status-pill {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  min-height: 1.75rem;
  gap: 0.3125rem;
  padding: 0.25rem 0.625rem;
  color: #ffffff;
  backdrop-filter: blur(0.625rem);
}

.status-pill .v-icon {
  color: currentColor;
}

.is-visible {
  background: rgba(0, 150, 64, 0.88);
}

.is-upcoming {
  background: rgba(217, 119, 6, 0.9);
}

.is-past {
  background: rgba(82, 82, 91, 0.9);
}

.slide-content {
  padding: 1rem;
}

.studio-chips {
  display: flex;
  min-height: 2rem;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-bottom: 0.875rem;
}

.mini-chip {
  min-height: 1.625rem;
  padding: 0.1875rem 0.5rem;
  border: 1px solid var(--border);
  color: #c7c7cb;
  background: rgba(255, 255, 255, 0.045);
}

.card-meta {
  justify-content: space-between;
  gap: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
  color: var(--muted);
}

.card-meta span {
  display: inline-flex;
  align-items: center;
  gap: 0.4375rem;
  font-size: 0.875rem;
}

.card-meta .v-icon {
  color: #8f9095;
}

.surface-empty {
  display: flex;
  min-height: 17rem;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 2.5rem;
  text-align: center;
}

.surface-empty > span {
  width: 3.5rem;
  height: 3.5rem;
  margin-bottom: 0.875rem;
  border-radius: 1rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.11);
}

.surface-empty > span .v-icon {
  color: currentColor;
}

.surface-empty h3,
.archive-card h3,
.section-heading h3 {
  margin: 0;
  color: var(--text);
  font-size: 1rem;
}

.surface-empty .secondary-action {
  margin-top: 1rem;
}

.archive-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
}

.archive-card {
  border-radius: 1rem !important;
  opacity: 0.82;
}

.archive-image,
.archive-placeholder {
  height: 6.5625rem;
}

.archive-card > div:last-child {
  padding: 0.75rem;
}

.archive-card > div > span {
  display: inline-flex;
  align-items: center;
  gap: 0.3125rem;
  color: var(--muted);
  font-size: 0.75rem;
  font-weight: 700;
}

.archive-card h3 {
  margin-top: 0.375rem;
}

.archive-pagination {
  margin-top: 1rem;
}

.editor-card {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  display: flex;
  width: 100%;
  max-height: calc(100vh - 2rem);
  max-height: calc(100dvh - 2rem);
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 1.5rem !important;
  color: var(--text);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.018), transparent 38%), rgba(28, 28, 29, 0.985) !important;
  font-size: 1rem;
}

.editor-header {
  flex: 0 0 auto;
  gap: 0.875rem;
  padding: 1.25rem 1.375rem;
  border-bottom: 1px solid var(--border);
  background: linear-gradient(115deg, rgba(0, 150, 64, 0.055), transparent 50%), rgba(255, 255, 255, 0.018);
}

.editor-mark {
  width: 3rem;
  height: 3rem;
  border: 1px solid rgba(0, 150, 64, 0.26);
  border-radius: 0.875rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.editor-mark .v-icon {
  color: currentColor;
}

.editor-header > div {
  min-width: 0;
}

.editor-close {
  margin-left: auto;
}

.editor-body {
  min-height: 0;
  flex: 1 1 auto;
  max-height: none;
  padding: 1.25rem !important;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.editor-section {
  height: 100%;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 1.0625rem;
  background: rgba(255, 255, 255, 0.028);
}

.section-heading {
  align-items: flex-start;
  gap: 0.625rem;
  margin-bottom: 1rem;
}

.section-heading > .v-icon {
  color: #58d388;
}

.modern-field {
  margin-bottom: 0.875rem;
}

.modern-field ::v-deep .v-input__slot {
  border-radius: 0.8125rem !important;
  background: rgba(255, 255, 255, 0.035) !important;
}

.modern-field ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.modern-field ::v-deep .v-label {
  color: #b8b8bc !important;
  font-size: 0.875rem;
}

.image-cropper {
  width: 100%;
  overflow: hidden;
  border-radius: 0.8125rem;
}

.format-hint,
.validation-hint {
  color: var(--muted);
  font-size: 0.75rem;
}

.format-hint {
  display: block;
  margin: 0.625rem 0 1rem;
  text-align: center;
}

.date-picker {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 0.8125rem;
  background: rgba(0, 0, 0, 0.12) !important;
}

.editor-actions {
  flex: 0 0 auto;
  min-height: 4.75rem;
  gap: 0.5rem;
  padding: 0.875rem 1.375rem !important;
  border-top: 1px solid var(--border);
  background: rgba(0, 0, 0, 0.14);
}

.cancel-action {
  min-height: 2.75rem;
  color: #ffffff !important;
  font-size: 0.875rem !important;
  text-transform: none;
}

@media (hover: hover) and (pointer: fine) {
  .slide-card:hover,
  .archive-card:hover {
    transform: translateY(-2px);
    border-color: var(--border-strong);
    background: var(--surface-strong) !important;
  }
}

@media (max-width: 78.9375rem) {
  .marketing-page {
    padding: 1.125rem;
  }

  .slide-grid,
  .archive-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 59.9375rem) {
  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 37.4375rem) {
  .marketing-page {
    padding: 0.75rem;
  }

  .metric-grid {
    gap: 0.5rem;
    margin: 0.75rem 0;
  }

  .metric-card {
    min-height: 6.5rem;
    align-items: flex-start;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    border-radius: 0.9375rem !important;
  }

  .metric-icon {
    width: 2.125rem;
    height: 2.125rem;
  }

  .metric-card small {
    display: none;
  }

  .content-surface {
    padding: 0.875rem;
    border-radius: 1.125rem !important;
  }

  .surface-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.625rem;
  }

  .slide-grid,
  .archive-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .editor-card {
    max-height: 100vh;
    max-height: 100dvh;
    border: 0;
    border-radius: 0 !important;
  }

  .editor-header {
    align-items: flex-start;
    padding: 1rem;
  }

  .editor-mark,
  .editor-header p,
  .validation-hint {
    display: none;
  }

  .editor-body {
    max-height: none;
    padding: 0.75rem !important;
  }

  .editor-actions {
    flex-wrap: wrap;
    padding: 0.75rem 1rem max(0.75rem, env(safe-area-inset-bottom)) !important;
  }

  .editor-actions .primary-action {
    flex: 1 1 100%;
    order: 3;
  }
}
</style>
